import { Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useCourses, useDeleteCourse } from '@/hooks/use-courses'
import { useTenantPath } from '@/lib/tenant'

export default function TeacherCoursesPage() {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useCourses({ search: search || undefined })
  const deleteCourse = useDeleteCourse()
  const tp = useTenantPath()

  const courses = data?.data ?? []

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`「${title}」を削除しますか？`)) return
    try {
      await deleteCourse.mutateAsync(id)
      toast.success('コースを削除しました')
    } catch {
      toast.error('削除に失敗しました')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">コース管理</h1>
        <Link to={tp('/teacher/courses/new')}>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            新規作成
          </Button>
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="コースを検索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={`course-skeleton-${i}`} className="h-20" />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <p className="text-muted-foreground">コースがありません。</p>
      ) : (
        <div className="space-y-3">
          {courses.map((course: any) => (
            <Card key={course.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base">{course.title}</CardTitle>
                  <Badge variant={course.isPublished ? 'default' : 'secondary'}>
                    {course.isPublished ? '公開' : '下書き'}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Link to={tp(`/teacher/courses/${course.id}/edit`)}>
                    <Button variant="outline" size="sm">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(course.id, course.title)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
